/** Canonical tab artwork (filled + outlined) lives in this coordinate system. */
export const TAB_BUTTON_DESIGN_WIDTH = 159
export const TAB_BUTTON_DESIGN_HEIGHT = 41

/** Horizontal slice bounds in design space: fixed end caps, stretchable middle. */
export const TAB_BUTTON_SLICE_LEFT = 52
export const TAB_BUTTON_SLICE_RIGHT = 107
const SLICE_MIDDLE = TAB_BUTTON_SLICE_RIGHT - TAB_BUTTON_SLICE_LEFT
const SLICE_CAP =
  TAB_BUTTON_SLICE_LEFT + (TAB_BUTTON_DESIGN_WIDTH - TAB_BUTTON_SLICE_RIGHT)

/** Filled tab (active) — single path in design coordinates. */
export const TAB_BUTTON_FILLED_PATH_D =
  'M107 2c.187.002.374.005.559.01H108c.404 0 .803.024 1.195.07 11.73.778 18.839 6.949 21.327 18.515C133.162 32.865 142.41 39 158.269 39H118l-75 .01V39H1c15.858 0 25.107-6.135 27.747-18.405C31.387 8.325 39.227 2.126 52.27 2l-.001.036c.242-.017.486-.026.732-.026l54-.001z'

/** Outlined tab — outer fill path. */
export const TAB_BUTTON_OUTLINED_OUTER_PATH_D =
  'M14.7 38c2.1-.7 4-1.7 5.7-2.8 4.8-3.2 8-8 9.3-14.4 1.3-5.9 3.7-10.3 7.5-13.3S46 3 52.2 3h55.3c.8 0 1.2 0 1.5.1 5.7.4 10.2 2 13.6 5 3.4 3 5.7 7.2 6.9 12.8 1.4 6.4 4.5 11.2 9.3 14.4 1.7 1.1 3.6 2 5.7 2.8'

/** Outlined tab — inner stroked path. */
export const TAB_BUTTON_OUTLINED_INNER_PATH_D =
  'M52.2 3h55.3c.8 0 1.2 0 1.5.1 5.7.4 10.2 2 13.6 5 3.4 3 5.7 7.2 6.9 12.8 1.4 6.4 4.5 11.2 9.3 14.4 1.7 1.1 3.6 2 5.7 2.8H14.7c2.1-.7 4-1.7 5.7-2.8 4.8-3.2 8-8 9.3-14.4C31 15 33.4 10.6 37.2 7.6S46 3.1 52.2 3z'

const VARIANT_BASE = {
  default: { width: TAB_BUTTON_DESIGN_WIDTH, height: TAB_BUTTON_DESIGN_HEIGHT },
}

/**
 * Resolved width prop: explicit `width` wins; otherwise intrinsic width from flags.
 */
export function getTabButtonEffectiveWidth(width, wide, ultraWide) {
  if (width !== undefined && width !== null && width !== '') {
    return width
  }
  if (ultraWide) {
    return 194
  }
  if (wide) {
    return 226
  }
  return undefined
}

/**
 * Width/height for TabButtonFilled / TabButtonOutlined SVGs.
 * With no `width`, returns intrinsic dimensions for the variant.
 * With a custom `width`, the SVG uses that width and keeps the variant’s
 * intrinsic height so the tab does not grow taller or shorter.
 * `useNonUniformScaling` is true when the graphic must stretch horizontally
 * (`preserveAspectRatio="none"` on the `<svg>`) — used only for the
 * non-sliced fallback when display width is not a known pixel value.
 */
export function resolveTabButtonSvgSize(width, variant = 'default') {
  const base = VARIANT_BASE[variant] || VARIANT_BASE.default
  if (width === undefined || width === null || width === '') {
    return {
      width: base.width,
      height: base.height,
      useNonUniformScaling: false,
    }
  }
  if (typeof width === 'number') {
    if (!Number.isFinite(width) || width <= 0) {
      return {
        width: base.width,
        height: base.height,
        useNonUniformScaling: false,
      }
    }
    return {
      width,
      height: base.height,
      useNonUniformScaling: width !== base.width,
    }
  }
  const str = String(width).trim()
  const pxMatch = str.match(/^([\d.]+)px$/i)
  if (pxMatch) {
    const w = parseFloat(pxMatch[1])
    if (Number.isFinite(w) && w > 0) {
      return {
        width: str,
        height: base.height,
        useNonUniformScaling: Math.abs(w - base.width) > 0.001,
      }
    }
  }
  return {
    width: str,
    height: base.height,
    useNonUniformScaling: true,
  }
}

/** Props to spread onto `<svg>`. */
export function tabButtonSvgAttrs(width, variant = 'default') {
  const { width: w, height: h, useNonUniformScaling } = resolveTabButtonSvgSize(
    width,
    variant,
  )
  return useNonUniformScaling
    ? { width: w, height: h, preserveAspectRatio: 'none' }
    : { width: w, height: h }
}

/** Pixel width we can counter-scale against (number or `NNpx` only). */
export function parseNumericDisplayWidth(width) {
  if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
    return width
  }
  if (typeof width === 'string') {
    const m = String(width)
      .trim()
      .match(/^([\d.]+)px$/i)
    if (m) {
      const w = parseFloat(m[1])
      if (Number.isFinite(w) && w > 0) {
        return w
      }
    }
  }
  return null
}

/**
 * Nine-slice horizontal stretch keeps end-cap curves in design space; requires a
 * known pixel width. Below the minimum, layout still clamps to the minimum.
 */
export function getTabButtonNineSliceMinWidth() {
  return SLICE_CAP
}

export function getTabButtonNineSliceMiddleScale(displayWidthPx) {
  const w = Math.max(SLICE_CAP, displayWidthPx)
  return (w - SLICE_CAP) / SLICE_MIDDLE
}

/**
 * Pixel width for nine-slice layout, or `null` when only non-px CSS lengths are used.
 */
export function getTabButtonDisplayPixelWidth(width) {
  const parsed = parseNumericDisplayWidth(width)
  if (parsed != null) {
    return parsed
  }
  const { width: w } = resolveTabButtonSvgSize(width, 'default')
  return typeof w === 'number' ? w : null
}

/**
 * When the tab SVG is stretched horizontally (`preserveAspectRatio="none"`),
 * wrap the label `<text>` in `<g transform={…}>` using this value so glyphs
 * stay proportionate. Returns `undefined` when no counter-scale is needed or
 * when `width` is not a known pixel width (e.g. `%`, `rem`).
 */
export function getTabButtonLabelGroupTransform(width, variant = 'default') {
  const { useNonUniformScaling } = resolveTabButtonSvgSize(width, variant)
  if (!useNonUniformScaling) {
    return undefined
  }
  const displayW = parseNumericDisplayWidth(width)
  if (displayW == null) {
    return undefined
  }
  const base = VARIANT_BASE[variant] || VARIANT_BASE.default
  const sx = base.width / displayW
  const cx = base.width / 2
  return `translate(${cx}, 0) scale(${sx}, 1) translate(${-cx}, 0)`
}
